'use client'

/**
 * useCollapsibleRail — durable, SSR-safe collapse state for a long-form
 * reader's side rail (the tutorial TOC / tools rails, the course-viewer
 * curriculum rail, the scene Spec sidebar).
 *
 * Two behaviors it exists to get right, both borrowed from the house
 * patterns in `useSessionState.ts` and `StepViewProvider`:
 *
 *  1. HYDRATION. These rails live in client components that Next.js still
 *     renders on the server for the initial HTML. Reading localStorage (or
 *     `window.innerWidth`) from a `useState` initializer would make the
 *     server HTML and first client render disagree → hydration mismatch. So
 *     the first render is always the deterministic default (expanded), and
 *     the real value is resolved from a layout effect after mount.
 *
 *  2. NO FLASH + SMART DEFAULT. That resolve runs in a layout effect (client
 *     only), flushed after commit but *before* paint, so the expanded default
 *     never reaches the screen when it shouldn't. Precedence is:
 *       saved localStorage preference  >  account default (`initialCollapsed`,
 *       an SSR-seeded per-user setting)  >  auto-collapse when viewport <
 *       `autoCollapseBelow`.
 *     The width default is what fixes the cramped narrow-desktop case
 *     (tablet-landscape / small laptops) without the reader having to touch
 *     anything; once they toggle, their choice is remembered and wins — even
 *     over their account default, so a manual collapse/expand on this device
 *     always wins locally.
 *
 *     `initialCollapsed` is the account "start this panel collapsed" preference,
 *     read server-side and passed in as a prop. When set (`true`/`false`) it is
 *     ALSO the `useState` seed, so the very first server HTML already renders in
 *     the right state (no flash, hydration matches). Pass `null`/`undefined`
 *     (anon, or "no explicit choice") to keep the plain width-based default.
 *
 * Durable (localStorage), not per-session: a reader who collapses a rail
 * wants it to stay collapsed next visit — same reasoning as
 * `theduchy.step-view-preference`. Every storage access is wrapped; Safari
 * private mode throws on access and enterprise policies can remove the API.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react'

// useLayoutEffect warns when React runs it on the server. It never actually
// fires during SSR, so swap in useEffect there purely to silence that.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

/** Below this viewport width, a rail with no saved preference starts
 *  collapsed. 1280px = the point below which the fixed-px rails squeeze the
 *  reading column (tablet-landscape 1024, small laptops 1280/1366). */
const DEFAULT_AUTO_COLLAPSE_BELOW = 1280

export interface UseCollapsibleRailResult {
  /** Whether the rail is currently collapsed. */
  collapsed: boolean
  /** Toggle collapsed/expanded and persist the choice. */
  toggle: () => void
  /**
   * True once the mount-time resolve has run. Consumers can gate a width
   * transition on this so the initial default→resolved correction doesn't
   * animate on first paint.
   */
  ready: boolean
}

export function useCollapsibleRail(
  storageKey: string,
  options: { autoCollapseBelow?: number; initialCollapsed?: boolean | null } = {},
): UseCollapsibleRailResult {
  const threshold = options.autoCollapseBelow ?? DEFAULT_AUTO_COLLAPSE_BELOW
  const initialCollapsed = options.initialCollapsed ?? null

  // First render: the account default when the user has one, else the
  // deterministic `false`. `initialCollapsed` is a server-passed prop (not
  // localStorage/window), so server HTML and first client render agree —
  // hydration stays clean and there's no flash for the account-default case.
  const [collapsed, setCollapsed] = useState(initialCollapsed ?? false)
  const [ready, setReady] = useState(false)

  // Mount-only resolve, before first paint: a saved localStorage choice wins
  // (a manual toggle on this device always wins locally), else the account
  // default, else the viewport-width smart default. Deps intentionally empty —
  // re-running would fight the reader's own subsequent toggles.
  useIsomorphicLayoutEffect(() => {
    let resolved = false
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored === 'collapsed') {
        setCollapsed(true)
        resolved = true
      } else if (stored === 'expanded') {
        setCollapsed(false)
        resolved = true
      }
    } catch {
      // localStorage unavailable (private mode, disabled) — fall through.
    }
    if (!resolved) {
      if (initialCollapsed != null) {
        setCollapsed(initialCollapsed)
      } else {
        try {
          setCollapsed(window.innerWidth < threshold)
        } catch {
          // no window (shouldn't happen in a layout effect) — keep default.
        }
      }
    }
    setReady(true)
    // Deps intentionally empty — mount-only resolve. (exhaustive-deps doesn't
    // inspect this aliased effect, so there's no directive to suppress.)
  }, [])

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(storageKey, next ? 'collapsed' : 'expanded')
      } catch {
        // persistence is a nicety; in-memory state is still correct.
      }
      return next
    })
  }, [storageKey])

  return { collapsed, toggle, ready }
}
